import * as React from 'react';
import { useMemo } from 'react';
import Viewport from '../../../../../widgets/Viewport';
import { Breadcrumb, BreadcrumbItem } from 'reactstrap';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { PageTitle } from '../../../../../widgets/PageTitle';
import ViewHeader from '../../../../../widgets/ViewHeader';
import { Form, Formik } from 'formik';
import { Container, FixedRightPanel, LeftPanel, PanelTitle } from './Form';
import { IPConfigFormInputs } from '../../forms/IPConfigFormInputs';
import { Button, Panel } from '@rtkwlf/ui';
import { OBHardwareIPConfig, URLConfigMap } from '../../../../../types';
import { ipConfigFormValidationSchema } from '../../../validationSchemas';
import { TextInput } from '../../../../../widgets/FormikInputFieldV2';

// Sauce https://css-tricks.com/snippets/css/prevent-long-urls-from-breaking-out-of-container/
const Url = styled.a`
  /* These are technically the same, but use both */
  overflow-wrap: break-word;
  word-wrap: break-word;

  -ms-word-break: break-all;
  /* This is the dangerous one in WebKit, as it breaks things wherever */
  word-break: break-all;
  /* Instead use this non-standard one: */
  word-break: break-word;

  /* Adds a hyphen where the word breaks, if supported (No Blink) */
  -ms-hyphens: auto;
  -moz-hyphens: auto;
  -webkit-hyphens: auto;
  hyphens: auto;
`;

const DeviceDetailsPanel = styled.div`
  max-width: 565px;
`;

export type DeviceConfigFormValues = {
  name: string;
  site: string;
  model: string;
  deploymentType: string;
} & OBHardwareIPConfig;

export const useQueryParamDeviceConfiguration = (): URLConfigMap | null => {
  const location = useLocation();

  return useMemo<URLConfigMap | null>(() => {
    const queryParams = new URLSearchParams(location.search);
    const encodedConfig = queryParams.get('c');

    if (!encodedConfig) {
      return null;
    }

    return JSON.parse(window.atob(encodedConfig)) as URLConfigMap;
  }, [location]);
};

export const EncodedConfigurationURL = ({
  deviceConfiguration,
  ipConfig,
}: {
  deviceConfiguration: URLConfigMap;
  ipConfig: DeviceConfigFormValues;
}) => {
  const configuration = ipConfig2UrlConfig(deviceConfiguration, ipConfig);
  const encodedConfig = window.btoa(JSON.stringify(configuration));

  // host is static (set by network interface)
  const url = `http://192.168.190.22/configure?${encodedConfig}`;

  const copyToClipboard = (txt: string) => () => {
    navigator.clipboard.writeText(txt);
  };

  return (
    <div>
      <Url id="encodedUrl" href={url}>
        {url}
      </Url>
      <Button onClick={copyToClipboard(url)}>Copy</Button>
    </div>
  );
};

const DeviceConfigViewport = ({ children }: { children: React.ReactNode }) => (
  <Viewport>
    <Breadcrumb>
      <BreadcrumbItem active>Customer Configured Appliance</BreadcrumbItem>
    </Breadcrumb>
    <PageTitle title="Customer Configured Appliance" />
    <ViewHeader title="Customer Configured Appliance" />
    {children}
  </Viewport>
);

export const urlConfig2IpConfig = (
  config: URLConfigMap,
): DeviceConfigFormValues => {
  const mgmt0 = config.mgmt0;

  return {
    name: config.shorthand,
    site: config.site,
    model: config.model,
    deploymentType: config.deployment_type,
    gateway: mgmt0.gateway,
    ipAddress: mgmt0.ip,
    isIPAutoConfig: mgmt0.dhcp,
    primaryDns:
      mgmt0.dns_nameservers.length >= 1 ? mgmt0.dns_nameservers[0] : '',
    secondaryDns:
      mgmt0.dns_nameservers.length >= 2 ? mgmt0.dns_nameservers[1] : '',
    subnetMask: mgmt0.netmask,
  };
};

export const ipConfig2UrlConfig = (
  config: URLConfigMap,
  values: DeviceConfigFormValues,
): URLConfigMap => {
  return {
    ...config,
    mgmt0: {
      dhcp: values.isIPAutoConfig,
      ip: values.ipAddress,
      netmask: values.subnetMask,
      gateway: values.gateway,
      dns_nameservers: values.isIPAutoConfig
        ? []
        : [values.primaryDns, values.secondaryDns],
    },
  };
};

const DeviceConfigView = () => {
  const deviceConfiguration = useQueryParamDeviceConfiguration();

  if (!deviceConfiguration) {
    return (
      <DeviceConfigViewport>
        <Panel>There is no configuration available.</Panel>
      </DeviceConfigViewport>
    );
  }

  return (
    <DeviceConfigViewport>
      <Formik
        initialValues={urlConfig2IpConfig(deviceConfiguration!)}
        onSubmit={() => {}}
        validationSchema={ipConfigFormValidationSchema}
      >
        {({ values, isValid }) => (
          <Form>
            <Container>
              <LeftPanel>
                <DeviceDetailsPanel>
                  <TextInput name="site" label="Site" disabled />
                  <TextInput name="name" label="Name" disabled />
                  <TextInput name="model" label="Model" disabled />
                  <TextInput
                    name="deploymentType"
                    label="Deployment Type"
                    disabled
                  />
                </DeviceDetailsPanel>

                <IPConfigFormInputs />
              </LeftPanel>
              <FixedRightPanel>
                <PanelTitle>New Config</PanelTitle>

                {!isValid ? (
                  <div>
                    Unable to create configuration URL due to invalid values.
                  </div>
                ) : (
                  <EncodedConfigurationURL
                    deviceConfiguration={deviceConfiguration}
                    ipConfig={values as DeviceConfigFormValues}
                  />
                )}
              </FixedRightPanel>
            </Container>
          </Form>
        )}
      </Formik>
    </DeviceConfigViewport>
  );
};

export default DeviceConfigView;
